import React, { useState, useEffect, useRef } from "react";
import { Button, Card, CardBody, CardHeader, Container, Modal, ModalBody, ModalFooter, ModalHeader, Form, FormGroup, Input, Label, Dropdown, DropdownToggle, DropdownMenu, DropdownItem, Spinner } from "reactstrap";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { Toast } from "primereact/toast";
import axios from "axios";
import { API_BASE_URL } from "../../../constants";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { Edit, MoreVertical, Trash } from "lucide-react";

const DepartmentList = () => {
    const [departments, setDepartments] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [modal, setModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [name, setName] = useState("");
    const [code, setCode] = useState("");
    const [description, setDescription] = useState("");
    const [parentId, setParentId] = useState("");
    const [selectedDepartment, setSelectedDepartment] = useState(null);
    const [departmentToDelete, setDepartmentToDelete] = useState(null);
    const [dropdownOpen, setDropdownOpen] = useState(null);
    const toggleDropdown = (id) => setDropdownOpen(dropdownOpen === id ? null : id);
    const toast = useRef(null);

    const toggle = () => setModal(!modal);
    const toggleDeleteModal = () => setDeleteModal(!deleteModal);


    const breadcrumbItems = [
        { title: "HR", link: "/" },
        { title: "Departments", link: "#" },
    ];

    const fetchDepartments = async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/departments/list`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            const departmentData = response.data.data || [];
            setDepartments(departmentData);
            setTotalRecords(departmentData.length);
        } catch (error) {
            console.error("Error fetching departments:", error);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchDepartments();
    }, []);

    const onPageChange = (event) => {
        setFirst(event.first);
        setRows(event.rows);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const data = {
            name,
            code,
            description,
            parent_id: parentId || null,
            office_id: 1,
            added_by: localStorage.getItem("user_id"),
        };

        setLoading(true);
        try {
            if (selectedDepartment) {
                await axios.post(`${API_BASE_URL}/departments/update/${selectedDepartment.id}`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Updated', detail: 'Department updated successfully', life: 3000 });
            } else {
                await axios.post(`${API_BASE_URL}/departments/add`, data, {
                    headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
                toast.current.show({ severity: 'success', summary: 'Added', detail: 'Department added successfully', life: 3000 });
            }
            setName("");
            setCode("");
            setDescription("");
            setParentId("");
            setSelectedDepartment(null);
            toggle();
            fetchDepartments();
        } catch (error) {
            console.error("Error details:", error.response?.data);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: error.response?.data?.message || 'Failed to save department', life: 3000 });
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        if (!departmentToDelete) return;
        try {
            await axios.delete(`${API_BASE_URL}/departments/delete/${departmentToDelete.id}`, {
                headers: { Authorization: "Bearer " + localStorage.getItem("token") },
            });
            toast.current?.show({ severity: "success", summary: "Deleted", detail: "Department deleted successfully", life: 3000 });
            fetchDepartments();
        } catch (error) {
            toast.current?.show({ severity: "error", summary: "Error", detail: "Failed to delete department", life: 3000 });
        }
        setDepartmentToDelete(null);
        toggleDeleteModal();
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Departments" breadcrumbItems={breadcrumbItems} />
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title mb-0 flex-grow-1">Departments List</h4>
                            <Button onClick={toggle} color="primary"><i className="mdi mdi-plus mr-1"> </i>Add Department</Button>
                        </CardHeader>
                        <CardBody>
                        <DataTable value={departments.slice(first, first + rows)} loading={loading} stripedRows>
                                <Column
                                    header="Si No"
                                    body={(rowData, { rowIndex }) => rowIndex + 1 + first}
                                />
                                <Column header="Name" field="name" sortable />
                                <Column header="Code" field="code" sortable />
                                <Column header="Parent" field="parent" sortable 
                                    body={(rowData) => rowData.parent ? rowData.parent.name : "N/A"}
                                />
                                <Column header="Description" field="description" sortable />
                                {/* Actions Column */}
                                <Column 
                                header="Actions" 
                                body={(rowData) => (
                                    <Dropdown 
                                    isOpen={dropdownOpen === rowData.id} 
                                    toggle={() => toggleDropdown(rowData.id)}
                                    >
                                    <DropdownToggle tag="button" className="btn btn-light">
                                        <MoreVertical size={20} />
                                    </DropdownToggle>
                                    <DropdownMenu>
                                        <DropdownItem 
                                        onClick={() => {
                                            setSelectedDepartment(rowData);
                                            setName(rowData.name);
                                            setCode(rowData.code);
                                            setDescription(rowData.description);
                                            setParentId(rowData.parent_id || "");
                                            toggle();
                                        }}
                                        >
                                        <Edit size={16} className="me-2" />
                                        Edit
                                        </DropdownItem>
                                        <DropdownItem 
                                        onClick={() => {
                                            setDepartmentToDelete(rowData);
                                            toggleDeleteModal();
                                        }}
                                        className="text-danger"
                                        >
                                        <Trash size={16} className="me-2" />
                                        Delete
                                        </DropdownItem>
                                    </DropdownMenu>
                                    </Dropdown>
                                )}
                                />
                            </DataTable>

                            <Paginator first={first} rows={rows} totalRecords={totalRecords} onPageChange={onPageChange} rowsPerPageOptions={[5, 10, 25, 50]} />
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <Modal isOpen={modal} centered toggle={toggle}>
                <Form onSubmit={handleSubmit}>
                    <ModalHeader toggle={toggle}>{selectedDepartment ? 'Edit Department' : 'Add Department'}</ModalHeader>
                    <ModalBody>
                        <FormGroup>
                            <Label>Name</Label>
                            <Input type="text" value={name} onChange={(e) => setName(e.target.value)} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Code</Label>
                            <Input type="text" value={code} onChange={(e) => setCode(e.target.value)} required />
                        </FormGroup>
                        <FormGroup>
                            <Label>Parent Department</Label>
                            <Input type="select" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                                <option value="">No Parent</option>
                                {departments.map(dept => (
                                    <option key={dept.id} value={dept.id}>{dept.name}</option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input type="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={toggle}>Cancel</Button>
                        <Button color="primary" type="submit">{selectedDepartment ? "Update" : "Add"}
                            {loading && <Spinner className="ms-1" size="sm" >Loading...</Spinner>}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={deleteModal} centered toggle={toggleDeleteModal}>
                <ModalHeader toggle={toggleDeleteModal}>Confirm Delete</ModalHeader>
                <ModalBody>
                    Are you sure you want to delete this department?
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={toggleDeleteModal}>Cancel</Button>
                    <Button color="danger" onClick={handleDelete}>Delete
                    {loading && <Spinner className="ms-1" size="sm" >Loading...</Spinner>}

                    </Button>
                </ModalFooter>
            </Modal>
            <Toast ref={toast} />
        </React.Fragment>
    );
};
export default DepartmentList;